import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import api from "@/lib/api";
import SiteFooter from "@/components/SiteFooter";
import ExitIntentOffer from "@/components/ExitIntentOffer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Zap, ArrowRight, Clock, BookOpen } from "lucide-react";

const handleLogin = () => {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog/posts").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  const canonical = typeof window !== "undefined" ? window.location.href : "";

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="blog-index">
      <Helmet>
        <title>Social Media Automation Guides by Niche | AutoGridSocial</title>
        <meta name="description" content="Practical guides on automating social media for indie devs, SaaS founders, creators, coaches, shops and more — the anti-slop way. From AutoGridSocial." />
        <link rel="canonical" href={canonical} />
      </Helmet>

      <header className="sticky top-0 z-40 border-b border-border/60 bg-background/80 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="blog-nav-home">
            <div className="h-8 w-8 rounded-lg bg-primary flex items-center justify-center"><Zap className="h-5 w-5 text-white" /></div>
            <span className="font-display font-bold text-lg tracking-tight">AutoGridSocial</span>
          </Link>
          <Button data-testid="blog-nav-cta" onClick={handleLogin} className="bg-primary hover:bg-primary/90 text-white text-sm">Get started</Button>
        </div>
      </header>

      <section className="border-b border-border/60">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <Badge className="mb-4 bg-primary/15 text-primary border-primary/30"><BookOpen className="h-3.5 w-3.5 mr-1.5" /> Guides</Badge>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">Social media automation, by niche</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto text-base sm:text-lg">
            No-fluff playbooks for staying consistent and on-brand — built for the way real operators actually work.
          </p>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
              className="group rounded-xl border border-border bg-card overflow-hidden flex flex-col card-hover">
              <div className="h-40 overflow-hidden bg-secondary">
                {p.banner_image_url && (
                  <img src={p.banner_image_url} alt={p.audience} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                )}
              </div>
              <div className="p-5 flex flex-col flex-1">
                <Badge variant="outline" className="w-fit text-[10px] border-border text-muted-foreground mb-3">{p.audience}</Badge>
                <h2 className="font-display font-semibold leading-snug mb-2 group-hover:text-primary transition-colors">{p.title}</h2>
                <p className="text-sm text-muted-foreground line-clamp-3 flex-1">{p.meta_description}</p>
                <div className="flex items-center justify-between mt-4 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> {p.read_minutes} min read</span>
                  <span className="flex items-center gap-1 text-primary">Read <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" /></span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <SiteFooter />
      <ExitIntentOffer />
    </div>
  );
}
